import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import AuthBrand from '@/components/AuthBrand';

export default function ResetPassword() {
  const [location] = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [token, setToken] = useState('');
  const [isValidToken, setIsValidToken] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    // Extract token from URL parameters
    // Note: wouter's useLocation() doesn't include query params, so we use window.location.search
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');

    console.log('Reset password page - Token extraction:', {
      windowSearch: window.location.search,
      extractedToken: resetToken ? `${resetToken.substring(0, 10)}...` : 'null',
    });

    if (resetToken) {
      setToken(resetToken);
    } else {
      setIsValidToken(false);
    }
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({
        variant: 'destructive',
        title: 'Missing fields',
        description: 'Please fill in all fields.',
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        variant: 'destructive',
        title: "Passwords don't match",
        description: 'Please make sure both passwords are the same.',
      });
      return;
    }

    if (password.length < 6) {
      toast({
        variant: 'destructive',
        title: 'Password too short',
        description: 'Password must be at least 6 characters long.',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiRequest('POST', '/api/auth/reset-password', { token, password });

      setIsReset(true);
      toast({
        title: 'Password reset successful',
        description: 'Your password has been updated. You can now sign in with your new password.',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Reset failed',
        description: error.message || 'Failed to reset password.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-hydro-100 flex items-center justify-center p-4 font-kumbh">
        <div className="w-full max-w-md">
          <AuthBrand subtitle="Corporate wellness through smart hydration" />

          <Card className="border-lilac-300 shadow-lg">
            <CardHeader>
              <CardTitle className="text-deep-800">Invalid Reset Link</CardTitle>
              <CardDescription className="text-deep-500">
                The password reset link is invalid or missing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-red-600 text-2xl">✗</span>
                </div>
                <p className="text-sm text-deep-600 mb-6">
                  The password reset link you clicked is either invalid, expired, or missing. Please
                  request a new password reset.
                </p>
              </div>

              <div className="space-y-3">
                <Link href="/forgot-password">
                  <Button
                    className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                    data-testid="button-request-new"
                  >
                    Request New Password Reset
                  </Button>
                </Link>

                <Link href="/login">
                  <Button
                    variant="ghost"
                    className="w-full text-deep-600 hover:text-purple-600"
                    data-testid="link-back-login"
                  >
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isReset) {
    return (
      <div className="min-h-screen bg-hydro-100 flex items-center justify-center p-4 font-kumbh">
        <div className="w-full max-w-md">
          <AuthBrand subtitle="Corporate wellness through smart hydration" />

          <Card className="border-lilac-300 shadow-lg">
            <CardHeader>
              <CardTitle className="text-deep-800">Password Reset Complete</CardTitle>
              <CardDescription className="text-deep-500">
                Your password has been successfully updated
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-sm text-deep-600 mb-6">
                  Your password has been reset successfully! You can now sign in with your new
                  password.
                </p>
              </div>

              <Link href="/login">
                <Button
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                  data-testid="button-sign-in"
                >
                  Sign In Now
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-hydro-100 flex items-center justify-center p-4 font-kumbh">
      <div className="w-full max-w-md">
        <AuthBrand subtitle="Corporate wellness through smart hydration" />

        <Card className="border-lilac-300 shadow-lg">
          <CardHeader>
            <CardTitle className="text-deep-800">Reset Your Password</CardTitle>
            <CardDescription className="text-deep-500">
              Please enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-deep-700">
                  New Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isSubmitting}
                    required
                    minLength={6}
                    data-testid="input-password"
                    className="bg-lilac-50 border-lilac-300"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-deep-500"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="button-toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-deep-500">Password must be at least 6 characters long</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-deep-700">
                  Confirm New Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isSubmitting}
                    required
                    minLength={6}
                    data-testid="input-confirm-password"
                    className="bg-lilac-50 border-lilac-300"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent text-deep-500"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    data-testid="button-toggle-confirm-password"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  type="submit"
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-full"
                  disabled={isSubmitting}
                  data-testid="button-reset-password"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Resetting Password...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </Button>

                <Link href="/login">
                  <Button
                    variant="ghost"
                    className="w-full text-deep-600 hover:text-purple-600"
                    data-testid="link-back-login"
                  >
                    Back to Sign In
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
